import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  Chip,
  Tooltip,
  CircularProgress,
  Typography,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SpeedIcon from '@mui/icons-material/Speed';
import VoiceInputButton from './VoiceInputButton';

/**
 * Enhanced search bar component with AI capabilities indicator
 * Shows which search mode is active and includes performance metrics
 * Now with option to try AI search if initial search has few results
 */
const EnhancedSearchBar = ({
  value,
  onChange,
  onSubmit,
  searchTier = 'keyword', // 'keyword', 'vector', or 'llm'
  searchTime = null,
  isSearching = false,
  showAiOption = false,
  onAiSearchRequest = null,
  placeholder = 'Search...',
  variant = 'outlined',
  fullWidth = true,
  autoFocus = false,
  disabled = false,
  elevation = 1,
  sx = {}
}) => {
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit && value.trim()) {
      onSubmit(value);
    }
  };

  // Clear search input
  const handleClear = () => {
    onChange('');
  };

  // Get tier icon and color
  const getTierDetails = () => {
    switch (searchTier) {
      case 'llm':
        return {
          icon: <AutoFixHighIcon fontSize="small" />,
          color: 'secondary',
          label: 'AI',
          tooltip: 'AI-powered semantic search'
        };
      case 'vector':
        return {
          icon: <SpeedIcon fontSize="small" />,
          color: 'primary',
          label: 'Vector',
          tooltip: 'Vector embedding search'
        };
      case 'keyword':
      default:
        return {
          icon: <LocalOfferIcon fontSize="small" />,
          color: 'default',
          label: 'Keyword',
          tooltip: 'Standard keyword search'
        };
    }
  };

  const { icon, color, label, tooltip } = getTierDetails();

  return (
    <Paper elevation={elevation} sx={{ p: 1, ...sx }}>
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', width: '100%' }}>
          <TextField
            fullWidth={fullWidth}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            variant={variant}
            autoFocus={autoFocus}
            disabled={disabled || isSearching}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {isSearching ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SearchIcon color="action" />
                  )}
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {value ? (
                    <IconButton
                      aria-label="clear search"
                      onClick={handleClear}
                      edge="end"
                      size="small"
                      sx={{ padding: '8px' }}
                      disabled={isSearching}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                  <VoiceInputButton
                    onInterim={(text) => onChange(text)}
                    onTranscript={(text) => {
                      onChange(text);
                      if (text.trim() && onSubmit) onSubmit(text);
                    }}
                    disabled={disabled || isSearching}
                    tooltip="Search by voice"
                  />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 2,
                height: 48,
                fontSize: '1rem'
              }
            }}
            sx={{ '& .MuiOutlinedInput-root': { pr: 0.5 } }}
          />
        </Box>
        
        {/* Search metrics row or AI suggestion */}
        {showAiOption && searchTier === 'keyword' ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Not finding what you're looking for?
            </Typography>
            <Button 
              size="small" 
              color="secondary" 
              variant="outlined"
              onClick={onAiSearchRequest}
              startIcon={<AutoFixHighIcon fontSize="small" />}
              disabled={isSearching}
            >
              Try AI Search
            </Button>
          </Box>
        ) : (searchTier !== 'keyword' || searchTime !== null) && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, alignItems: 'center' }}>
            <Tooltip title={tooltip}>
              <Chip
                icon={icon}
                label={label}
                color={color}
                size="small"
                variant="outlined"
                sx={{ height: 24 }}
              />
            </Tooltip>
            
            {searchTime !== null && (
              <Tooltip title="Search execution time">
                <Chip
                  icon={<SpeedIcon fontSize="small" />}
                  label={`${searchTime}ms`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 24 }}
                />
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default EnhancedSearchBar;
